import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  Grid,
  Slide,
  Zoom,
  Fade,
  Collapse,
  Stack,
  Tab,
  Tabs,
  ButtonBase,
  alpha,
  LinearProgress,
  Dialog,
  ListItemButton,
  styled,
  useTheme,
  useMediaQuery,
  keyframes,
  Snackbar,
  Chip,
  Tooltip,
  Link
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  PictureAsPdf as PictureAsPdfIcon,
  History as HistoryIcon,
  Add as AddIcon,
  ChatBubbleOutline,
  Close,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  CloudDownload as CloudDownloadIcon,
} from "@mui/icons-material";
import { 
  Sparkles, 
  Wand2, 
  ImagePlus, 
  Lightbulb, 
  Loader, 
  Zap, 
  Scissors, 
  Layers, 
  Scale, 
  FileOutput, 
  Award, 
  Brush,
  PaintBucket,
  Camera,
  ImageDown,
  Images,
  Star
} from 'lucide-react';
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import AuthContext from "../context/AuthContext";
import ReactMarkdown from "react-markdown";
import ConversationHistory from "./ConversationHistory";
import QuickActionsPanel from "./QuickActionsPanel";

// ======== ANIMATIONS ========
const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// ======== STYLED COMPONENTS ========

// Modified RootContainer to be truly full width and add substantial bottom padding
const RootContainer = styled(Box)(({ theme }) => ({
  marginTop: 0, // Remove top margin
  marginBottom: 0, // Remove bottom margin
  height: "calc(100vh - 60px)", // Adjusted to leave 60px at bottom
  maxHeight: "calc(100vh - 60px)",
  width: '100%', // Full width
  display: "flex",
  flexDirection: "column",
  animation: `${fadeIn} 0.4s ease-out`,
  overflow: 'hidden',
  paddingBottom: theme.spacing(4), // Increased padding at the bottom
}));

// Modified ContentContainer for internal padding with adjusted height
const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: 'calc(100% - 20px)', // Leave some space at the bottom
  padding: theme.spacing(0, 2), // Add horizontal padding for content
  paddingTop: theme.spacing(10), // Keep space for navbar
  paddingBottom: theme.spacing(3), // Add more padding at the bottom
  boxSizing: 'border-box',
}));

const MessageBubble = styled(Paper)(({ isUser, theme }) => ({
  padding: theme.spacing(2.5),
  maxWidth: "80%",
  borderRadius: isUser ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
  backgroundColor: isUser 
    ? alpha(theme.palette.primary.main, 0.12)
    : alpha(theme.palette.background.paper, 0.9),
  color: isUser ? theme.palette.text.primary : theme.palette.text.primary,
  boxShadow: isUser 
    ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
    : `0 2px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
  position: "relative",
  borderLeft: isUser ? 'none' : `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
  animation: `${slideUp} 0.3s ease-out`,
  transition: "all 0.2s ease-in-out",
  overflow: "hidden",
  "&:hover": {
    boxShadow: isUser 
      ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
      : `0 4px 16px ${alpha(theme.palette.grey[500], 0.15)}`,
    "& .message-actions": {
      opacity: 1,
    },
  },
}));

const MessageWrapper = styled(Box)(({ isUser }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: isUser ? "flex-end" : "flex-start",
  marginBottom: 24,
  width: "100%",
  position: "relative", // Important for proper positioning of children
}));

const MessageHeader = styled(Box)(({ theme, isUser }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: 8,
  color: theme.palette.text.secondary,
  justifyContent: isUser ? "flex-end" : "flex-start",
  gap: 8,
}));

const MessageTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  opacity: 0.7,
}));

const MessageActions = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  display: "flex",
  gap: 4,
  opacity: 0,
  transition: "opacity 0.2s ease",
  zIndex: 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(4px)",
  borderRadius: 8,
  padding: "2px",
}));

// Scroll to Bottom Button - completely repositioned to avoid overlap
const ScrollButton = styled(Button)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(9), // Position well above the input area
  right: theme.spacing(4),
  borderRadius: "50%",
  minWidth: "auto",
  width: 48,
  height: 48,
  padding: 0,
  boxShadow: theme.shadows[4],
  zIndex: 100, // Higher z-index to ensure it's above everything
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

// Enhanced Thinking Indicator Component
const ThinkingWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '500px',
  margin: theme.spacing(1, 0),
  alignSelf: 'flex-start',
}));

const ThinkingCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2.5),
  borderRadius: 24,
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
  backdropFilter: 'blur(8px)',
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.dark, 0.1)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 50%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 2s infinite`,
    opacity: 0.8,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  color: theme.palette.primary.contrastText,
  animation: `${pulse} 1.5s infinite ease-in-out`,
}));

const ContentWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  position: 'relative',
  zIndex: 1,
});

const TextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const DotsWrapper = styled(Box)({
  display: 'flex',
  gap: 4,
  marginLeft: 8,
});

const LoadingDot = styled(Box)(({ theme, delay = 0 }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `${bounce} 1s infinite`,
  animationDelay: `${delay}ms`,
}));

// Simplified Image Link for processed images
const ProcessedImageLink = ({ url, label, theme }) => {
  if (!url) return null;
  
  return (
    <Box sx={{ 
      mb: 1.5, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      borderRadius: 1,
      p: 1.5,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    }}>
      <ImageIcon fontSize="small" color="primary" />
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
        {label || "Processed Image"}
      </Typography>
      <Button
        size="small"
        endIcon={<OpenInNewIcon />}
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ borderRadius: 20 }}
      >
        View
      </Button>
    </Box>
  );
};

// Enhanced Processing Status Component
const ProcessingStatus = ({ progress, qualityScore, batchStatus }) => {
  const theme = useTheme();
  
  // Default to 0 if progress is undefined
  const normalizedProgress = progress || 0;
  
  // Show loading indicator if progress is missing
  const isLoading = progress === undefined;

  return (
    <Box sx={{ 
      mt: 2, 
      p: 2, 
      borderRadius: 2, 
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
    }}>
      <Typography 
        variant="body2" 
        color="textSecondary" 
        sx={{ 
          mb: 1, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1
        }}
      >
        {isLoading ? (
          <Loader size={16} className="animate-spin" />
        ) : (
          <Zap size={16} />
        )}
        {isLoading ? "Processing..." : "Enhancement Progress:"}
      </Typography>
      
      <LinearProgress
        variant={isLoading ? "indeterminate" : "determinate"}
        value={normalizedProgress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          "& .MuiLinearProgress-bar": {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      />
      
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" color="textSecondary">
          {isLoading ? "Please wait..." : `${normalizedProgress}%`}
        </Typography>
        {qualityScore !== undefined && (
          <Chip 
            size="small"
            label={`Quality Score: ${qualityScore}`}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      
      {batchStatus && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Batch Status: {batchStatus}
        </Typography>
      )}
    </Box>
  );
};

// Enhanced File Preview Component
const FilePreview = ({ file, preview, onRemove }) => {
  const isImage = file.type.startsWith("image/");
  const theme = useTheme();

  return (
    <Zoom in={true}>
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          width: 120,
          height: 120,
          borderRadius: 0,
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {isImage ? (
          <img
            src={preview}
            alt={file.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.error.main, 0.1),
            }}
          >
            <PictureAsPdfIcon color="error" sx={{ fontSize: 36 }} />
            <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>
              PDF Document
            </Typography>
          </Box>
        )}
        {onRemove && (
          <IconButton
            size="small"
            onClick={() => onRemove(file)}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255,0,0,0.7)",
              },
              width: 28,
              height: 28,
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "4px 8px",
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            color: "white",
            fontSize: "10px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {file.name}
        </Box>
      </Paper>
    </Zoom>
  );
};

// Enhanced Drag and Drop Area
const DropZone = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  textAlign: "center",
  backgroundColor: isDragActive 
    ? alpha(theme.palette.primary.main, 0.05) 
    : alpha(theme.palette.grey[50], 0.8),
  transition: theme.transitions.create(["border-color", "background-color", "transform", "box-shadow"], {
    duration: 200,
  }),
  cursor: "pointer",
  height: 180,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

// Custom Styled Input Component
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2), // Add bottom margin to the text field
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    padding: theme.spacing(1, 1, 1, 2),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(8px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.95),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    '& fieldset': {
      borderWidth: 1,
      borderColor: alpha(theme.palette.divider, 0.3),
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderWidth: 1,
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(1.5, 1),
  },
}));

// ======== HELPER FUNCTIONS ========

// Helper function to resize an image
const resizeImage = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const MAX_DIMENSION = 2048;
        let width = img.width;
        let height = img.height;

        if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
          resolve(file);
          return;
        }

        if (width > height) {
          if (width > MAX_DIMENSION) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          },
          "image/jpeg",
          0.9
        );
      };
    };
  });
};

// Improved Parsing Logic for Model Responses
const parseModelResponse = (response) => {
  // Safety check for null or undefined responses
  if (!response) {
    return { text: "", searchSuggestions: [], generationPrompts: [], enhancedImages: [] };
  }
  
  const searchSuggestionsSet = new Set();
  const generationPromptsSet = new Set();
  const enhancedImages = [];
  let cleanText = response;

  // Parse Search Suggestions with quotes - Case 1: With explicit "Search Suggestion:" label
  const searchPattern = /Search Suggestion:\s*["']([^"']+)["']/gi;
  let match;
  while ((match = searchPattern.exec(response))) {
    searchSuggestionsSet.add(match[1]);
  }

  // Case 2: With "Search Suggestions:" list format
  const searchListPattern = /Search Suggestions?:\s*([\s\S]*?)(?=\n\n|$)/i;
  const searchListMatch = response.match(searchListPattern);
  if (searchListMatch) {
    const suggestions = searchListMatch[1]
      .split("\n")
      .map((line) => {
        // Handle quoted format
        const quoted = line.match(/["']([^"']+)["']/);
        if (quoted) return quoted[1];
        
        // Handle list format (bullet points, numbers, etc.)
        return line.replace(/^[-•*\d\s.]+/, "").trim();
      })
      .filter(Boolean);

    suggestions.forEach((s) => searchSuggestionsSet.add(s));
    cleanText = cleanText.replace(searchListMatch[0], "");
  }
  
  // Case 3: With "Reference Images:" list format (alternative phrasing)
  const referenceImagesPattern = /Reference Images?:\s*([\s\S]*?)(?=\n\n|$)/i;
  const referenceImagesMatch = response.match(referenceImagesPattern);
  if (referenceImagesMatch) {
    const suggestions = referenceImagesMatch[1]
      .split("\n")
      .map((line) => {
        const quoted = line.match(/["']([^"']+)["']/);
        if (quoted) return quoted[1];
        return line.replace(/^[-•*\d\s.]+/, "").trim();
      })
      .filter(Boolean);

    suggestions.forEach((s) => searchSuggestionsSet.add(s));
    cleanText = cleanText.replace(referenceImagesMatch[0], "");
  }

  // Parse Generation Prompts
  // Case 1: With explicit "Generation Prompt:" label
  const genPattern = /Generation Prompt:\s*["']([^"']+)["']/gi;
  while ((match = genPattern.exec(response))) {
    generationPromptsSet.add(match[1]);
  }
  
  // Case 2: With "Generation Prompts:" list format
  const genListPattern = /Generation Prompts?:\s*([\s\S]*?)(?=\n\n|$)/i;
  const genListMatch = response.match(genListPattern);
  if (genListMatch) {
    const prompts = genListMatch[1]
      .split("\n")
      .map((line) => {
        const quoted = line.match(/["']([^"']+)["']/);
        if (quoted) return quoted[1];
        return line.replace(/^[-•*\d\s.]+/, "").trim();
      })
      .filter(Boolean);

    prompts.forEach((p) => generationPromptsSet.add(p));
    cleanText = cleanText.replace(genListMatch[0], "");
  }
  
  // Case 3: With "Generate Elements:" list format (alternative phrasing)
  const generateElementsPattern = /Generate Elements?:\s*([\s\S]*?)(?=\n\n|$)/i;
  const generateElementsMatch = response.match(generateElementsPattern);
  if (generateElementsMatch) {
    const prompts = generateElementsMatch[1]
      .split("\n")
      .map((line) => {
        const quoted = line.match(/["']([^"']+)["']/);
        if (quoted) return quoted[1];
        return line.replace(/^[-•*\d\s.]+/, "").trim();
      })
      .filter(Boolean);

    prompts.forEach((p) => generationPromptsSet.add(p));
    cleanText = cleanText.replace(generateElementsMatch[0], "");
  }

  // Format search results
  const searchResults = Array.from(searchSuggestionsSet).map((query) => ({
    query,
    results: [], // Will be populated when search API is called
  }));

  return {
    text: cleanText,
    searchSuggestions: Array.from(searchSuggestionsSet),
    generationPrompts: Array.from(generationPromptsSet),
    enhancedImages,
    searchResults,
  };
};

// ThinkingIndicator Component
const ThinkingIndicator = () => {
  const messages = [
    "Analyzing design elements...",
    "Processing your request...",
    "Generating creative ideas...",
    "Exploring design possibilities...",
    "Optimizing visual elements...",
    "Crafting design solutions..."
  ];

  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ThinkingWrapper>
      <ThinkingCard>
        <ContentWrapper>
          <IconWrapper>
            <Wand2 size={24} />
          </IconWrapper>
          
          <TextWrapper>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'primary.main',
                  letterSpacing: '0.015em',
                }}
              >
                Design Assistant
              </Typography>
              <Typography 
                component="span" 
                sx={{ 
                  color: 'primary.main',
                  animation: `${pulse} 2s infinite ease-in-out`,
                }}
              >
                ✨
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ color: (theme) => alpha(theme.palette.primary.main, 0.8) }}
              >
                {messages[messageIndex]}
              </Typography>
              <DotsWrapper>
                {[0, 1, 2].map((i) => (
                  <LoadingDot key={i} delay={i * 150} />
                ))}
              </DotsWrapper>
            </Box>
          </TextWrapper>
        </ContentWrapper>
      </ThinkingCard>
    </ThinkingWrapper>
  );
};

// Enhanced Message Component with attachments inside the bubble
const Message = ({ message, onSearch, onGenerate }) => {
  const theme = useTheme();
  const isUser = message.role === "user";
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  
  const handleCopyContent = () => {
    navigator.clipboard.writeText(message.content);
    setShowSnackbar(true);
    setMenuAnchor(null);
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Improved function to render attachments
  const renderAttachment = (att, idx) => {
    // Check if this is an image using multiple methods
    const isImage = att.type?.startsWith('image/') || 
                   att.type === "image" ||
                   att.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
    
    if (isImage) {
      return (
        <Box key={idx} sx={{ minWidth: 100, mb: 1.5 }}>
          <img
            src={att.url}
            alt={att.name || "Attached image"}
            style={{
              maxWidth: "100%",
              maxHeight: "200px",
              borderRadius: "4px",
              display: "block",
            }}
          />
        </Box>
      );
    } else if (att.type === "pdf" || att.url?.endsWith('.pdf')) {
      return (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            mb: 1.5,
          }}
        >
          <PictureAsPdfIcon color="error" />
          <Typography variant="body2" color="textPrimary" sx={{ flex: 1, fontWeight: 500 }}>
            {att.name || "PDF Document"}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            sx={{ borderRadius: 20 }}
          >
            View
          </Button>
        </Box>
      );
    } 
    // For enhanced images or processed images
    else if (att.type === "enhanced_image" || att.type?.includes("_image")) {
      // Create an array to hold unique links
      const uniqueLinks = [];
      
      // Only add the original image link if it exists and isn't already added
      if ((att.beforeUrl || att.original_url) && uniqueLinks.length === 0) {
        uniqueLinks.push(
          <ProcessedImageLink 
            key={`original-${idx}`}
            url={att.beforeUrl || att.original_url} 
            label="Original Image" 
            theme={theme} 
          />
        );
      }
      
      // Only add the enhanced image link if it exists and isn't a duplicate
      if ((att.afterUrl || att.processed_url) && uniqueLinks.length <= 1) {
        uniqueLinks.push(
          <ProcessedImageLink 
            key={`enhanced-${idx}`}
            url={att.afterUrl || att.processed_url} 
            label="Enhanced Image" 
            theme={theme} 
          />
        );
      }
      
      // If we have no specific links but a generic url, add that
      if (uniqueLinks.length === 0 && att.url) {
        uniqueLinks.push(
          <ProcessedImageLink 
            key={`processed-${idx}`}
            url={att.url} 
            label={att.type === "color_palette" ? "Color Palette" : "Processed Image"} 
            theme={theme} 
          />
        );
      }
      
      return (
        <Box key={idx}>
          {uniqueLinks}
        </Box>
      );
    }
    
    return null;
  };

  // Process message content to extract and display search suggestions and generation prompts
  const MessageContent = ({ content, processing, attachments }) => {
    const { text, searchSuggestions, generationPrompts } = parseModelResponse(content);
    
    const handleSearch = (query) => {
      onSearch(query);
    };
    
    const handleGenerate = (prompt) => {
      onGenerate(prompt);
    };
    
    return (
      <Box>
        {/* Render attachments for user messages inside the bubble */}
        {isUser && attachments && attachments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {attachments.map((att, idx) => renderAttachment(att, idx))}
          </Box>
        )}
      
        <ReactMarkdown>{text}</ReactMarkdown>
        
        {processing && <ProcessingStatus {...processing} />}
        
        {/* Render processed image links for assistant messages */}
        {!isUser && attachments && attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {attachments.map((att, idx) => renderAttachment(att, idx))}
          </Box>
        )}
        
        {(searchSuggestions?.length > 0 || generationPrompts?.length > 0) && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {searchSuggestions?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ 
                  mb: 1, 
                  display: "flex", 
                  alignItems: "center",
                  gap: 0.8
                }}>
                  <SearchIcon fontSize="small" />
                  Reference Images:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {searchSuggestions.map((query, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => handleSearch(query)}
                      startIcon={<SearchIcon />}
                      sx={{ 
                        textTransform: "none",
                        borderRadius: 20,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      {query}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
            
            {generationPrompts?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ 
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8
                }}>
                  <ImagePlus size={16} />
                  Generate Elements:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {generationPrompts.map((prompt, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => handleGenerate(prompt)}
                      startIcon={<Sparkles size={16} />}
                      sx={{ 
                        textTransform: "none",
                        borderRadius: 20,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      {prompt}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };
  
  return (
    <MessageWrapper isUser={isUser}>
      <MessageHeader isUser={isUser}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 500,
            color: isUser ? 'text.secondary' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          {isUser ? (
            <>
              You
            </>
          ) : (
            <>
              <Wand2 size={14} />
              Design Assistant
            </>
          )}
        </Typography>
        <MessageTimestamp>{formatDate(message.timestamp)}</MessageTimestamp>
      </MessageHeader>
      
      <MessageBubble isUser={isUser}>
        <MessageActions className="message-actions">
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopyContent}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MessageActions>
        
        <MessageContent 
          content={message.content} 
          processing={message.processing}
          attachments={message.attachments}
        />
      </MessageBubble>
      
      <Snackbar
        open={showSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        message="Message copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </MessageWrapper>
  );
};

// ======== MAIN COMPONENT ========

const DesignGenerator = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Conversation persistence and history states
  const [showHistory, setShowHistory] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, message: "", severity: "success" });

  // Sidebar visibility states
  const [showLeftPanel, setShowLeftPanel] = useState(!isMobile);
  const [showRightPanel, setShowRightPanel] = useState(!isMobile);

  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedPreviews, setUploadedPreviews] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [recentChats, setRecentChats] = useState([]);

  // Setup initial panel states based on screen size
  useEffect(() => {
    if (isMobile) {
      setShowLeftPanel(false);
      setShowRightPanel(false);
    } else {
      setShowLeftPanel(true);
      setShowRightPanel(true);
    }
  }, [isMobile]);

  // Toggle panels
  const toggleLeftPanel = () => {
    setShowLeftPanel(!showLeftPanel);
  };

  const toggleRightPanel = () => {
    setShowRightPanel(!showRightPanel);
  };

  // Fetch recent chats from API
  const fetchRecentChats = async () => {
    try {
      const response = await api.get('/design-copilot/recent-chats');
      if (response.data.success) {
        setRecentChats(response.data.recent_chats || []);
      }
    } catch (error) {
      console.error('Error fetching recent chats:', error);
      setFeedback({
        show: true,
        message: "Failed to load recent conversations",
        severity: "error"
      });
    }
  };

  // Navigation functions
  const executeSearch = (query) => {
    showFeedback("Navigating to search...", "info");
    navigate("/web-search-images", {
      state: {
        searchQuery: query,
        from: "design-copilot",
      },
    });
  };

  const executeGeneration = (prompt) => {
    showFeedback("Setting up image generation...", "info");
    navigate("/generate-images", {
      state: {
        generationPrompt: prompt,
        from: "design-copilot",
      },
    });
  };

  // Show feedback toast
  const showFeedback = (message, severity = "success") => {
    setFeedback({
      show: true,
      message,
      severity
    });
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setFeedback(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Scroll button visibility handler
  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, clientHeight } = e.target;
    const bottomThreshold = 100;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > bottomThreshold);
  };

  // Scroll to bottom
  const scrollToBottom = (smooth = true) => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  // Improved load chat history function
  const loadChatHistory = async (sessionId) => {
    try {
      setLoading(true);
      showFeedback("Loading conversation...", "info");
      
      const response = await api.get(`/design-copilot/chat/${sessionId}/history`);
      
      if (response.data.messages) {
        // Track unique attachment URLs to prevent duplicates
        const uniqueAttachmentMap = new Map();
        
        // Map the messages to include all necessary properties
        const formattedMessages = response.data.messages.map(msg => {
          // Process attachments, removing duplicates and fixing type detection
          const processedAttachments = msg.attachments 
            ? msg.attachments
                // Filter out duplicates using URL as key
                .filter(att => {
                  const url = att.url || att.processed_url || att.afterUrl;
                  if (!url || uniqueAttachmentMap.has(url)) return false;
                  uniqueAttachmentMap.set(url, true);
                  return true;
                })
                // Map to standardized format
                .map((att) => {
                  // Determine file type using multiple checks
                  let fileType = att.type || "unknown";
                  
                  // Force to "image" if URL has image extension or type contains image
                  if (att.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || 
                      fileType.startsWith('image/') ||
                      fileType === "image") {
                    fileType = "image";
                  }
                  // Force to "pdf" if URL ends with .pdf
                  else if (att.url?.endsWith('.pdf')) {
                    fileType = "pdf";
                  }
                  
                  return {
                    ...att,
                    type: fileType,
                    url: att.url,
                    beforeUrl: att.before_url || att.beforeUrl || att.original_url,
                    afterUrl: att.after_url || att.afterUrl || att.processed_url,
                    original_url: att.original_url,
                    processed_url: att.processed_url,
                    qualityScore: att.quality_score || att.qualityScore,
                  };
                })
            : [];

          return {
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString(),
            attachments: processedAttachments,
            processing: msg.processing_status || null
          };
        });

        setMessages(formattedMessages);
        setSessionId(sessionId);
        scrollToBottom(false);
        
        showFeedback("Conversation loaded successfully", "success");
      }
    } catch (error) {
      console.error("Error loading chat history:", error);
      setError("Failed to load conversation history");
      localStorage.removeItem("designChatSessionId");
      setSessionId(null);
      setMessages([]);
      showFeedback("Failed to load conversation", "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial load to check for saved session
  useEffect(() => {
    const initialLoad = async () => {
      const savedSessionId = localStorage.getItem("designChatSessionId");
      if (savedSessionId) {
        await loadChatHistory(savedSessionId);
      }
    };
    
    initialLoad();
  }, []); // Empty dependency array for initial load only

  // Save session on component unmount
  useEffect(() => {
    return () => {
      if (sessionId) {
        localStorage.setItem("designChatSessionId", sessionId);
      }
    };
  }, [sessionId]);

  // Setup global clipboard paste functionality 
  useEffect(() => {
    const handleGlobalPaste = async (e) => {
      // Only process if not already in a text input field
      if (document.activeElement.tagName === "TEXTAREA" || 
          document.activeElement.tagName === "INPUT") {
        return;
      }
      
      if (!e.clipboardData || !e.clipboardData.items) return;
      
      const items = e.clipboardData.items;
      const imageItems = [];
      
      // Collect all image items from clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            imageItems.push(blob);
          }
        }
      }
      
      if (imageItems.length === 0) return;
      
      // Process the pasted images
      try {
        // Convert clipboard images to File objects with proper names
        const clipboardFiles = imageItems.map((blob, index) => {
          const timestamp = new Date().getTime();
          const filename = `pasted-image-${timestamp}-${index}.png`;
          return new File([blob], filename, { type: blob.type });
        });
        
        // Process the files
        const processedFiles = await Promise.all(
          clipboardFiles.map(async (file) => {
            if (file.type.startsWith('image/')) {
              return await resizeImage(file);
            }
            return file;
          })
        );
        
        // Update state with the new images
        setUploadedFiles((prev) => [...prev, ...processedFiles]);
        const newPreviews = processedFiles.map((file) => URL.createObjectURL(file));
        setUploadedPreviews((prev) => [...prev, ...newPreviews]);
        
        // Show feedback
        showFeedback(`${processedFiles.length} image${processedFiles.length > 1 ? 's' : ''} pasted from clipboard`, "success");
        
        // Ensure the left panel is open to show the images
        if (!showLeftPanel) {
          setShowLeftPanel(true);
        }
      } catch (error) {
        console.error("Error processing pasted images:", error);
        showFeedback("Failed to process pasted images", "error");
      }
    };
    
    // Add the global paste event listener
    window.addEventListener('paste', handleGlobalPaste);
    
    return () => {
      window.removeEventListener('paste', handleGlobalPaste);
    };
  }, [showLeftPanel]); // Include showLeftPanel in dependencies

  // Start new conversation
  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("designChatSessionId");
    showFeedback("Started a new design conversation", "success");
  };

  // Enhanced file upload handling
  const handleFileUpload = async (files) => {
    try {
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      const validFiles = Array.from(files).filter((file) => {
        if (file.size > maxFileSize) {
          showFeedback(`File ${file.name} is too large. Maximum size is 10MB.`, "error");
          return false;
        }
        
        if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
          showFeedback(`File ${file.name} is not supported. Please upload images or PDFs.`, "error");
          return false;
        }
        
        return true;
      });

      if (validFiles.length === 0) return;

      showFeedback(`Processing ${validFiles.length} file${validFiles.length > 1 ? 's' : ''}...`, "info");
      
      const processedFiles = await Promise.all(
        validFiles.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return await resizeImage(file);
          }
          return file;
        })
      );

      setUploadedFiles((prev) => [...prev, ...processedFiles]);
      const newPreviews = processedFiles.map((file) => URL.createObjectURL(file));
      setUploadedPreviews((prev) => [...prev, ...newPreviews]);
      
      showFeedback(`${processedFiles.length} file${processedFiles.length > 1 ? 's' : ''} ready to upload`, "success");
      
      // Ensure the left panel is open to show the uploaded files
      if (!showLeftPanel) {
        setShowLeftPanel(true);
      }
    } catch (error) {
      console.error("Error processing files:", error);
      showFeedback("Failed to process uploaded files", "error");
    }
  };

  // Enhanced drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const { files } = e.dataTransfer;
    handleFileUpload(files);
  };

  // Updated sendMessage function
  const sendMessage = async () => {
    if (!inputMessage.trim() && uploadedFiles.length > 0) {
      showFeedback("Please add a message describing what you'd like to do with the image(s)", "error");
      return;
    }
    if (!inputMessage.trim() && uploadedFiles.length === 0) return;

    try {
      setLoading(true);
      setIsThinking(true);
      setError("");

      const formData = new FormData();
      formData.append("message", inputMessage);

      if (sessionId) {
        formData.append("session_id", sessionId);
      }

      // Add uploaded files to formData
      uploadedFiles.forEach((file) => {
        formData.append("files", file);
      });

      // Create file data objects for attachments
      const fileAttachments = uploadedFiles.map((file, index) => ({
        type: file.type.startsWith("image/") ? "image" : "pdf",
        url: uploadedPreviews[index],
        name: file.name
      }));

      // Add user message with attachments
      const newUserMessage = {
        role: "user",
        content: inputMessage,
        timestamp: new Date().toISOString(),
        attachments: fileAttachments
      };

      setMessages((prev) => [...prev, newUserMessage]);
      setInputMessage("");
      setUploadedFiles([]);
      setUploadedPreviews([]);
      scrollToBottom(false);

      // Send message to API
      const response = await api.post("/design-copilot/chat", formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percentCompleted}%`);
        },
        timeout: 5 * 60 * 1000, // 5 minutes
      });

      console.log("API Response:", response.data);

      // Process the response
      // Format and standardize attachment data
      const processedAttachments = [];
      
      // Handle enhanced images if present
      if (response.data.processing_results && response.data.processing_results.processed_images) {
        for (const img of response.data.processing_results.processed_images) {
          processedAttachments.push({
            type: img.processing_type || "enhanced_image",
            original_url: img.original_url,
            processed_url: img.processed_url,
            beforeUrl: img.original_url,
            afterUrl: img.processed_url,
            url: img.processed_url,
            success: img.success,
            qualityScore: img.quality_score
          });
        }
      }
      
      // Process attachment data from messages to avoid duplicates
      if (response.data.messages && response.data.messages.length > 0) {
        const assistantMessage = response.data.messages.find(m => m.role === "assistant");
        if (assistantMessage && assistantMessage.attachments) {
          const seen = new Set(); // Track URLs we've already added to avoid duplicates
          
          assistantMessage.attachments.forEach(att => {
            // Create a unique key for this attachment
            const originalUrl = att.original_url || att.beforeUrl;
            const processedUrl = att.processed_url || att.afterUrl || att.url;
            
            // Only add if we haven't seen this pair of URLs
            const key = `${originalUrl || ''}-${processedUrl || ''}`;
            if (!seen.has(key)) {
              seen.add(key);
              
              processedAttachments.push({
                ...att,
                // Standardize URL properties
                original_url: att.original_url || att.beforeUrl, 
                processed_url: att.processed_url || att.afterUrl,
                beforeUrl: att.original_url || att.beforeUrl, 
                afterUrl: att.processed_url || att.afterUrl,
                url: att.url || att.processed_url || att.afterUrl
              });
            }
          });
        }
      }

      // Create assistant message with processed attachments
      const newAssistantMessage = {
        role: "assistant",
        content: response.data.response || "I've processed your request.",
        timestamp: new Date().toISOString(),
        attachments: processedAttachments,
        processing: response.data.processing_status || null,
      };

      // Update messages
      setMessages((prev) => [...prev, newAssistantMessage]);

      // Save session ID if provided
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem("designChatSessionId", response.data.session_id);
      }

      // Scroll to the new message
      scrollToBottom();
    } catch (err) {
      console.error("Error sending message:", err);
      const errorMessage =
        err.code === "ECONNABORTED"
          ? "The operation is taking longer than expected. Please try with fewer images."
          : err.message === "Network Error"
          ? "Connection error. Please check your internet connection."
          : err.response?.data?.detail || "Failed to send message. Please try again.";

      setError(errorMessage);
      showFeedback(errorMessage, "error");
    } finally {
      setLoading(false);
      setIsThinking(false);
    }
  };

  // Handle design action click
  const handleQuickAction = (action) => {
    setInputMessage(action.placeholder);
    document.querySelector('textarea')?.focus();
  };

  return (
    <RootContainer>
      <ContentContainer>
        {/* Top Navigation with New Chat and Past Conversations */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            pb: 2,
          }}
        >
          <Typography 
            variant="h5" 
            color="primary"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              fontWeight: 600
            }}
          >
            <Wand2 size={28} />
            Design Copilot
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button 
              startIcon={<AddIcon />} 
              onClick={startNewConversation} 
              variant="outlined"
              sx={{ 
                borderRadius: 20,
                px: 2,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              New Chat
            </Button>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => {
                setShowHistory(true);
                fetchRecentChats();
              }}
              variant="contained"
              sx={{ 
                borderRadius: 20,
                px: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                }
              }}
            >
              Past Designs
            </Button>
            
            {!isMobile && (
              <IconButton
                onClick={toggleRightPanel}
                color="primary"
                sx={{ 
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  backgroundColor: showRightPanel ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <Wand2 size={18} />
              </IconButton>
            )}
            
            {isMobile && (
              <IconButton
                onClick={toggleLeftPanel}
                color="primary"
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <AttachFileIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ height: "100%", maxHeight: "calc(100vh - 180px)", overflow: "hidden", margin: 0, width: '100%' }}>
          {/* Left Panel - Attachments */}
          {showLeftPanel && (
            <Grid item xs={12} md={3} sx={{ height: "100%" }}>
              <Paper 
                elevation={3} 
                sx={{ 
                  height: "100%", 
                  p: 2, 
                  overflow: "auto",
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                    Attachments
                  </Typography>
                  
                  {isMobile && (
                    <IconButton 
                      size="small" 
                      onClick={toggleLeftPanel}
                    >
                      <Close />
                    </IconButton>
                  )}
                </Box>
                
                <DropZone
                  isDragActive={isDragActive}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <AttachFileIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1.5 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Drop files here or click to upload
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    You can also paste images from clipboard
                  </Typography>
                </DropZone>

                <Box sx={{ mt: 2, overflow: "auto", flex: 1 }}>
                  {uploadedFiles.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>Files to upload ({uploadedFiles.length})</span>
                        <Button 
                          size="small" 
                          startIcon={<DeleteIcon fontSize="small" />} 
                          color="error"
                          variant="text"
                          onClick={() => {
                            setUploadedFiles([]);
                            setUploadedPreviews([]);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear all
                        </Button>
                      </Typography>
                      <Grid container spacing={1}>
                        {uploadedFiles.map((file, index) => (
                          <Grid item xs={6} key={index}>
                            <FilePreview
                              file={file}
                              preview={uploadedPreviews[index]}
                              onRemove={() => {
                                setUploadedFiles((files) => files.filter((_, i) => i !== index));
                                setUploadedPreviews((previews) => previews.filter((_, i) => i !== index));
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Typography variant="body2">
                        No files attached yet
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Main Chat Area */}
          <Grid item xs={12} md={showLeftPanel ? (showRightPanel ? 7 : 9) : (showRightPanel ? 9 : 12)} sx={{ height: "100%" }}>
            <Paper 
              elevation={3} 
              sx={{ 
                height: "100%", 
                display: "flex", 
                flexDirection: "column",
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(10px)",
                position: "relative"
              }}
            >
              {/* Toggle buttons for mobile */}
              {isMobile && !showLeftPanel && (
                <IconButton
                  size="small"
                  onClick={toggleLeftPanel}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    backdropFilter: 'blur(5px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    }
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              )}
              
              {/* Add right panel toggle for mobile */}
              {isMobile && !showRightPanel && (
                <IconButton
                  size="small"
                  onClick={toggleRightPanel}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    backdropFilter: 'blur(5px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    }
                  }}
                >
                  <Wand2 size={18} />
                </IconButton>
              )}
              
              {/* Chat Messages */}
              <Box
                ref={chatContainerRef}
                onScroll={handleScroll}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {messages.length === 0 ? (
                  <Box 
                    sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 4,
                      p: 4,
                      textAlign: 'center',
                      color: theme.palette.text.secondary
                    }}
                  >
                    <Box sx={{ 
                      width: 100, 
                      height: 100, 
                      borderRadius: '50%', 
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Wand2 size={48} color={alpha(theme.palette.primary.main, 0.7)} />
                    </Box>
                    <Box>
                      <Typography variant="h5" color="primary.main" sx={{ mb: 2, fontWeight: 600 }}>
                        Welcome to Design Copilot
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, maxWidth: 600 }}>
                        Upload images and describe what you'd like to do with them, or ask for design advice, color schemes, layout ideas, and more.
                      </Typography>
                      
                      <Box sx={{ maxWidth: 600, mx: 'auto', display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          startIcon={<AttachFileIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{ 
                            borderRadius: 20, 
                            px: 3, 
                            py: 1.2,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                          }}
                        >
                          Upload Images to Get Started
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  messages.map((message, index) => (
                    <Message
                      key={index}
                      message={message}
                      onSearch={executeSearch}
                      onGenerate={executeGeneration}
                    />
                  ))
                )}
                
                {isThinking && <ThinkingIndicator />}
              </Box>

              {/* Scroll to Bottom Button */}
              <Fade in={showScrollButton}>
                <ScrollButton variant="contained" onClick={() => scrollToBottom()}>
                  <ArrowUpwardIcon />
                </ScrollButton>
              </Fade>

              {/* Input Area */}
              <Box sx={{ 
                p: 3, // Increased padding
                pb: 4, // Extra padding at the bottom
                borderTop: 1, 
                borderColor: alpha(theme.palette.divider, 0.2),
                backgroundColor: alpha(theme.palette.background.paper, 0.4),
                marginTop: 'auto' // Push to bottom of container
              }}>
                {error && (
                  <Box sx={{ mb: 2 }}>
                    <Alert 
                      severity="error" 
                      onClose={() => setError("")}
                      sx={{ borderRadius: 2 }}
                    >
                      {error}
                    </Alert>
                  </Box>
                )}
                
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs>
                    <StyledTextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder={uploadedFiles.length > 0 
                        ? "Describe what you'd like to do with these images..." 
                        : "Ask a design question or upload an image..."
                      }
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <IconButton 
                            edge="end" 
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ mr: 0.5 }}
                          >
                            <AttachFileIcon color="action" />
                          </IconButton>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <IconButton 
                      color="primary" 
                      onClick={sendMessage} 
                      disabled={loading}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        transition: 'all 0.2s ease',
                        transform: loading ? 'scale(0.95)' : 'scale(1)',
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    </IconButton>
                  </Grid>
                </Grid>
                
                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} attached
                    </Typography>
                    <Button 
                      variant="text" 
                      size="small" 
                      onClick={() => {
                        setUploadedFiles([]);
                        setUploadedPreviews([]);
                      }}
                      startIcon={<DeleteIcon fontSize="small" />}
                      sx={{ ml: 1, textTransform: 'none' }}
                    >
                      Clear all
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Right Panel - Quick Actions */}
          {showRightPanel && (
            <Grid item xs={12} md={2} sx={{ height: "100%" }}>
              <Paper 
                elevation={3} 
                sx={{ 
                  height: "100%", 
                  overflow: "auto",
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}
                  >
                    <Wand2 size={16} />
                    Quick Tools
                  </Typography>
                  
                  {isMobile && (
                    <IconButton 
                      size="small" 
                      onClick={toggleRightPanel}
                    >
                      <Close />
                    </IconButton>
                  )}
                </Box>
                
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                  <QuickActionsPanel 
                    onActionClick={handleQuickAction}
                  />
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </ContentContainer>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        accept="image/*,application/pdf"
        onChange={(e) => handleFileUpload(e.target.files)}
      />

      {/* Conversation History Dialog */}
      <Dialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backdropFilter: "blur(10px)",
          }
        }}
      >
        <ConversationHistory
          chats={recentChats}
          onSelectConversation={(sessionId) => {
            loadChatHistory(sessionId);
            setShowHistory(false);
          }}
          onClose={() => setShowHistory(false)}
        />
      </Dialog>
      
      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.show}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, show: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={feedback.severity}
          onClose={() => setFeedback({ ...feedback, show: false })}
          sx={{ 
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            width: '100%',
            alignItems: 'center'
          }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </RootContainer>
  );
};

export default DesignGenerator;